import React, { useMemo } from 'react';

import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, Button, IconButton, List, ListItemButton, ListItemText, Typography } from '@mui/material';

import { ChatThread } from './AiChatContext';
import { getThreadPreview, isEmptyChatThread } from './chatThreadUtils';

interface AiChatConversationListProps {
  threads: Record<string, ChatThread>;
  activeThreadId: string | null;
  onSelect: (threadId: string) => void;
  onDelete: (threadId: string) => void;
  onNewChat: () => void;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(timestamp: number): number {
  const date = new Date(timestamp);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

function getThreadGroupLabel(timestamp: number): string {
  const today = startOfDay(Date.now());
  const day = startOfDay(timestamp);
  const diffDays = Math.round((today - day) / DAY_MS);

  if (diffDays <= 0) {
    return 'Today';
  }
  if (diffDays === 1) {
    return 'Yesterday';
  }
  if (diffDays <= 7) {
    return 'Previous 7 days';
  }
  if (diffDays <= 30) {
    return 'Previous 30 days';
  }
  return new Date(timestamp).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

export default function AiChatConversationList({
  threads,
  activeThreadId,
  onSelect,
  onDelete,
  onNewChat,
}: AiChatConversationListProps) {
  const groupedThreads = useMemo(() => {
    const sorted = Object.values(threads)
      .filter((thread) => !isEmptyChatThread(thread))
      .sort((a, b) => b.updatedAt - a.updatedAt);
    const groups: Array<{ label: string; threads: Array<ChatThread> }> = [];
    for (const thread of sorted) {
      const label = getThreadGroupLabel(thread.updatedAt);
      const last = groups[groups.length - 1];
      if (last && last.label === label) {
        last.threads.push(thread);
      } else {
        groups.push({ label, threads: [thread] });
      }
    }
    return groups;
  }, [threads]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
        <Button
          variant="text"
          size="small"
          startIcon={<AddCommentOutlinedIcon />}
          onClick={onNewChat}
          fullWidth
          sx={{ justifyContent: 'flex-start', px: 1.25, py: 0.75, color: 'text.primary', fontWeight: 500 }}
        >
          New chat
        </Button>
      </Box>

      {groupedThreads.length === 0 ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ px: 2, py: 1.5, display: 'block', textAlign: 'center' }}
        >
          No history yet.
        </Typography>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 1, pb: 1 }}>
          {groupedThreads.map((group) => (
            <Box key={group.label} sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  px: 1.25,
                  pt: 1,
                  pb: 0.5,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                }}
              >
                {group.label}
              </Typography>
              <List dense disablePadding>
                {group.threads.map((thread) => {
                  const isActive = thread.id === activeThreadId;
                  return (
                    <ListItemButton
                      key={thread.id}
                      selected={isActive}
                      onClick={() => onSelect(thread.id)}
                      sx={{
                        py: 0.75,
                        px: 1.25,
                        borderRadius: 1.5,
                        mb: 0.25,
                        '& .ai-chat-delete': {
                          opacity: 0,
                          transition: 'opacity 120ms ease-in-out',
                        },
                        '&:hover .ai-chat-delete, &.Mui-selected .ai-chat-delete': {
                          opacity: 1,
                        },
                      }}
                    >
                      <ListItemText
                        primary={thread.title}
                        secondary={getThreadPreview(thread.messages)}
                        slotProps={{
                          primary: {
                            variant: 'body2',
                            fontWeight: isActive ? 600 : 500,
                            noWrap: true,
                          },
                          secondary: {
                            variant: 'caption',
                            color: 'text.secondary',
                            noWrap: true,
                          },
                        }}
                      />
                      <IconButton
                        edge="end"
                        size="small"
                        className="ai-chat-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(thread.id);
                        }}
                        title="Delete conversation"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
